import { useState, useEffect, useCallback } from 'react';
import {
  normalizeOrgBillingJson,
  type OrgBillingRecord,
  type OrgBillingPayload,
} from '../utils/orgBillingAnalysisUtils';

const PATHS = [
  '/json-data/project_billing_analysis.json',
  '/project_billing_analysis.json',
] as const;

interface Result extends OrgBillingPayload {
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

/** UC-T7 lineage — project_billing_analysis.json from gold_fact_project_user_monthly_*. */
export function useProjectBillingAnalysis(): Result {
  const [records, setRecords] = useState<OrgBillingRecord[]>([]);
  const [yearsAvailable, setYearsAvailable] = useState<number[]>([]);
  const [yearMonthsAvailable, setYearMonthsAvailable] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  const refetch = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    (async () => {
      let lastErr: Error | null = null;
      for (const path of PATHS) {
        try {
          const r = await fetch(path);
          if (!r.ok) {
            lastErr = new Error(`${path}: ${r.status}`);
            continue;
          }
          const json = await r.json();
          const norm = normalizeOrgBillingJson(json);
          if (!cancelled) {
            setRecords(norm.records);
            setYearsAvailable(norm.years_available);
            setYearMonthsAvailable(norm.year_months_available ?? []);
            setLoading(false);
          }
          return;
        } catch (e) {
          lastErr = e instanceof Error ? e : new Error(String(e));
        }
      }
      if (!cancelled) {
        setError(
          lastErr?.message ||
            'Failed to load project_billing_analysis.json',
        );
        setRecords([]);
        setYearsAvailable([]);
        setYearMonthsAvailable([]);
        setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [tick]);

  return {
    records,
    years_available: yearsAvailable,
    year_months_available: yearMonthsAvailable,
    loading,
    error,
    refetch,
  };
}
